import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  ElementRef,
  HostBinding,
  Input,
  OnInit,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { SEnumType } from '@summit/core';
import { SummitDisposing } from '@summit/models';
import {
  CompactType,
  DisplayGrid,
  Draggable,
  GridType,
  GridsterConfig,
  GridsterModule,
  PushDirections,
  Resizable
} from 'angular-gridster2';
import { BehaviorSubject, Observable, combineLatest, distinctUntilChanged, map, startWith, takeUntil } from 'rxjs';
import { SDashboardItem } from '../../models/s-dashboard-item';
import { LayoutService } from '../../services/layout.service';

interface Safe extends GridsterConfig {
  draggable: Draggable;
  resizable: Resizable;
  pushDirections: PushDirections;
}
/* based on tutorial from https://www.freakyjolly.com/draggable-grid-blocks-using-angular-gridster2-tutorial/ */

@Component({
  selector: 's-ui-dashboard',
  templateUrl: './s-ui-dashboard.component.html',
  styleUrls: ['./s-ui-dashboard.component.scss'],
  standalone: true,

  imports: [GridsterModule, NgTemplateOutlet, AsyncPipe],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SUiDashboardComponent<T extends SEnumType> extends SummitDisposing implements OnInit, AfterViewInit {
  @HostBinding('class.flex') classFlex = true;
  @HostBinding('class.h-full') classHeightFull = true;
  //@HostBinding('style.grow.inherit') classFlexInherit = true;
  options: Safe;
  @Input() cellHeight = 150;
  @Input() cellWidth = 150;
  @Input() dashboardItems$: Observable<SDashboardItem<T>[]>;
  @Input() devMode: boolean;
  @ViewChild('gridWrapper', { static: false }) gridWrapper: ElementRef;
  @ContentChild('widget', { static: true }) widget: TemplateRef<unknown>;
  formattedDashboardItemsBs: BehaviorSubject<SDashboardItem<T>[]> = new BehaviorSubject<SDashboardItem<T>[]>([]);
  formattedDashboardItems$: Observable<SDashboardItem<T>[]> = this.formattedDashboardItemsBs.asObservable();
  dashboardHeight: number;
  dashboardWidth: number;

  constructor(
    private layoutService: LayoutService,
    private cd: ChangeDetectorRef
  ) {
    super();
  }

  ngOnInit() {
    this.options = {
      disableWarnings: true, //TODO: change to false and fix the warnings on resize creating overlaps
      gridType: GridType.ScrollVertical,
      compactType: CompactType.None,
      margin: 5,
      outerMargin: true,
      outerMarginTop: null,
      outerMarginRight: null,
      outerMarginBottom: null,
      outerMarginLeft: null,
      useTransformPositioning: true,
      mobileBreakpoint: 0,
      useBodyForBreakpoint: false,
      minCols: 12,
      maxCols: 12,
      minRows: 1,
      maxRows: 100,
      maxItemCols: 100,
      minItemCols: 1,
      maxItemRows: 100,
      minItemRows: 1,
      maxItemArea: 2500,
      minItemArea: 1,
      defaultItemCols: 1,
      defaultItemRows: 1,
      fixedColWidth: this.cellWidth,
      fixedRowHeight: this.cellHeight,
      keepFixedHeightInMobile: false,
      keepFixedWidthInMobile: false,
      scrollSensitivity: 10,
      scrollSpeed: 20,
      enableEmptyCellClick: false,
      enableEmptyCellContextMenu: false,
      enableEmptyCellDrop: false,
      enableEmptyCellDrag: false,
      enableOccupiedCellDrop: false,
      emptyCellDragMaxCols: 50,
      emptyCellDragMaxRows: 50,
      ignoreMarginInRow: false,
      draggable: {
        enabled: false
      },
      resizable: {
        enabled: false
      },
      swap: false,
      pushItems: true,
      disablePushOnDrag: false,
      disablePushOnResize: false,
      pushDirections: { north: false, east: false, south: true, west: false },
      pushResizeItems: false,
      displayGrid: this.devMode ? DisplayGrid.Always : DisplayGrid.OnDragAndResize,
      disableWindowResize: false,
      scrollToNewItems: false
    };
  }

  ngAfterViewInit(): void {
    const margin = this.options.margin ?? 0;
    (
      combineLatest([
        this.dashboardItems$.pipe(startWith([])),
        this.layoutService.screenSize$.pipe(
          map(() => this.gridWrapper.nativeElement.offsetWidth),
          //tap((width) => console.log(`resizeGrid width: ${width} / ${this.cellWidth + this.options.margin}`)),
          map((width: number) => Math.floor(width / (this.cellWidth + margin))),
          //tap((colCount) => console.log(`resizeGrid column count:  ${colCount}`)),
          map((colCount: number) => Math.max(colCount, 3)), //limit to 3 master columns
          distinctUntilChanged()
        )
      ]) as Observable<[SDashboardItem<T>[], number]>
    )
      .pipe(takeUntil(this.disposing$))
      .subscribe(([items, viewColCount]) => {
        //Reformat items
        let formattedItems = items.map((item) => new SDashboardItem(item));
        //Now that we have a copy of formatted items
        const currentNeededColCount = items.reduce(
          (acc, cur) => (acc < cur.gridData.x + cur.gridData.cols ? cur.gridData.x + cur.gridData.cols : acc),
          0
        );
        //console.log(`resizeGrid currentColCount: ${currentColCount}`);
        this.options.minCols = viewColCount;
        this.options.maxCols = viewColCount;
        //        console.log(`resizeGrid Cols: ${this.options.maxCols}`);
        this.options.api?.optionsChanged?.();
        if (currentNeededColCount > viewColCount) {
          //console.log(`resizeGrid resizing to ${colCount} columns`);
          const currentMaxCols = this.options.maxCols ?? viewColCount;
          let rowItems: SDashboardItem<T>[] = [];
          let remainingItems = formattedItems;
          //const colShift = 0;
          while (remainingItems.length > 0) {
            //Next row
            const minRow = remainingItems.reduce((acc, cur) => (acc < cur.gridData.y ? acc : cur.gridData.y), 999);
            const maxRowSpan = remainingItems
              .filter((item) => item.gridData.y === minRow)
              .reduce((acc, cur) => (acc < cur.gridData.rows ? cur.gridData.rows : acc), 0);
            // const maxVirtualCols = remainingItems
            //   .filter((item) => item.y === minRow)
            //   .reduce((acc, cur) => (acc < cur.x + cur.rows ? cur.x + cur.rows : acc), 0);
            //console.log(`resizeGrid max: ${maxVirtualCols}`);
            rowItems = [...rowItems, ...remainingItems.filter((item) => item.gridData.y <= minRow + maxRowSpan - 1)];
            remainingItems = remainingItems.filter((item) => item.gridData.y > minRow + maxRowSpan - 1);
            let nextRowColShift = 0;
            let nextRowShiftHeight = 0;
            //console.log(`resizeGrid scale: ${scale}`);
            rowItems.forEach((item) => {
              //console.log(`starting with ${item['item']} y: ${item.y} x: ${item.x}`);
              if (item.gridData.x >= currentMaxCols) {
                //console.log(`resizeGrid ${item['item']} x: ${item.x} virtualColCount: ${virtualColCount}`);
                item.gridData.x = item.gridData.x - currentMaxCols;
                // console.log(
                //   `resizeGrid ${item['item']} looking for conflicts in x: ${item.x} colspan : ${item.cols} to ${
                //     item.x + item.cols
                //   }`
                // );
                const colConflicts = rowItems.filter(
                  (rowItem) =>
                    rowItem.gridData.x >= item.gridData.x &&
                    rowItem.gridData.x <= item.gridData.x + item.gridData.cols - 1 &&
                    rowItem['item'] != item['item']
                );
                const currentMaxRowInCol = colConflicts.reduce(
                  (acc, cur) => (acc < cur.gridData.y + cur.gridData.rows ? cur.gridData.y + cur.gridData.rows : acc),
                  0
                );
                // console.log(
                //   `resizeGrid ${item['item']} y: ${item.y} current Max in row: ${currentMaxRowInCol}`,
                //   colConflicts,
                //   rowItems
                // );
                item.gridData.y = currentMaxRowInCol;
                nextRowColShift = Math.max(nextRowColShift, item.gridData.cols);
                nextRowShiftHeight = Math.max(nextRowShiftHeight, item.gridData.rows);
              } else {
                //Fit into current col
                const colConflicts = rowItems.filter(
                  (rowItem) =>
                    rowItem.gridData.x >= item.gridData.x &&
                    rowItem.gridData.x <= item.gridData.x + item.gridData.cols - 1 &&
                    rowItem['item'] != item['item']
                );
                //console.log(`resizeGrid ${item['item']} colConflicts:`, item, colConflicts);
                const currentMaxRowInCol = colConflicts
                  .filter((rowItem) => rowItem.gridData.y < item.gridData.y) //only above me
                  .reduce(
                    (acc, cur) => (acc < cur.gridData.y + cur.gridData.rows ? cur.gridData.y + cur.gridData.rows : acc),
                    0
                  );
                //Shift to Bottom
                if (item.gridData.y < currentMaxRowInCol) item.gridData.y = currentMaxRowInCol;
              }
            });
            const nextRowStart = minRow + maxRowSpan;
            //remainingItems = remainingItems.slice(0, 1);
            let currentColShift = nextRowColShift;
            remainingItems.forEach((item) => {
              if (item.gridData.y >= nextRowStart) {
                if (item.gridData.x > currentColShift) {
                  currentColShift -= item.gridData.x;
                }
                item.gridData.x = item.gridData.x + currentNeededColCount;
              }
              // console.log(`Finished with ${item['item']} y: ${item.gridData.y} x: ${item.gridData.x}`);
            });
            //remainingItems = [];
          }

          formattedItems = rowItems;
          // console.log(
          //   `resizeGrid sorted formattedItems:`,
          //   formattedItems.map((item) => item['item']),
          //   formattedItems
          // );
        }
        // console.log(
        //   `resizeGrid formattedItems:`,
        //   formattedItems.map((item) => item['item']),
        //   formattedItems
        // );
        this.configureGridSize(formattedItems);
        this.changedOptions();
        this.formattedDashboardItemsBs.next(formattedItems);
        this.cd.detectChanges();
      });
  }

  configureGridSize(items: SDashboardItem<T>[]): void {
    //console.log(`resizeGrid pre resize items:`, items);
    const rowCount = items.reduce(
      (acc, cur) => (acc < cur.gridData.y + cur.gridData.rows ? cur.gridData.y + cur.gridData.rows : acc),
      0
    );
    //console.log(`resizeGrid post resize rowCount: ${rowCount}`);
    const margin = this.options.margin ?? 0;
    this.dashboardHeight = (rowCount + 1) * this.cellHeight + (rowCount + 1) * margin;
  }

  changedOptions(): void {
    if (this.options.api && this.options.api.optionsChanged) {
      this.options.api.optionsChanged();
    }
  }

  removeItem($event: MouseEvent | TouchEvent, _item): void {
    $event.preventDefault();
    $event.stopPropagation();
    //this.dashboardItems.splice(this.dashboardItems.indexOf(item), 1);
  }

  addItem(): void {
    //this.dashboardItems.push({ x: 0, y: 0, cols: 1, rows: 1 });
  }
}
